import * as React from 'react';
import {flowRight} from 'lodash';
import {emptyObject, emptyArray} from '@packages/utilities/fp/utils';
import {assoc} from '@packages/utilities/key-map';
import {relative, pad, type Box} from '@packages/utilities/box';
import {useTheme} from '@packages/utilities/styled';
import {cutout} from '@packages/utilities/svg-path';

import {unselectable} from './constants/Styles';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

const roundAndPad = flowRight(assoc('borderRadius')(2.5), pad(2));

const nullBox = {left: 0, top: 0, width: 0, height: 0} as const;

export type Props = {
  viewport: Box;
  anchor: Box;
  padded?: boolean;
  isMask: boolean;
  bright?: boolean;
  style?: CSSProperties;
  /**
   * Makes the anchor element transparent (aka not rendered under mask)
   *
   * Defaults to `true`
   */
  transparentAnchor?: boolean;
  /** Allows pointer events to pass through the mask */
  allowMouseEventsInWindow?: boolean;
  /**
   * Dimensions to "cut out" a part of the mask and make it transparent. In
   * other words, imagine that you're in construction and you need to install
   * a window in a wall. You'll need to "cut out" a hole. This prop describes
   * where and how big.
   */
  cutouts?: Array<Box>;
  onClick?: (arg1: React.MouseEvent<EventTarget>) => void;
  // untyped properties discovered when making this type exact
  onWheel?: React.WheelEventHandler<SVGSVGElement>;
  onContextMenu?: (arg1: React.MouseEvent<EventTarget>) => void;
  rect?: Box;
};

/**
 * @deprecated use `Mask` from `@packages/systems/spring` instead
 */
const Mask = ({
  // @ts-expect-error - TS7031 - Binding element 'viewport' implicitly has an 'any' type.
  viewport,
  anchor = nullBox,
  padded = false,
  allowMouseEventsInWindow = false,
  transparentAnchor = true,
  // @ts-expect-error - TS7031 - Binding element 'isMask' implicitly has an 'any' type.
  isMask,
  // @ts-expect-error - TS7031 - Binding element 'bright' implicitly has an 'any' type.
  bright,
  style = emptyObject,
  cutouts = emptyArray,
  ...rest
}) => {
  const {
    colors: {backgroundTransparentMask, backgroundMask, brightShadow},
  } = useTheme();

  const relativeToViewport = relative(viewport);
  const relativeAnchor = relativeToViewport(anchor);
  const background = bright ? brightShadow : backgroundMask;
  const pointerEvents =
    style.pointerEvents || (allowMouseEventsInWindow ? 'none' : undefined);

  return (
    <div
      style={{
        position: 'absolute',
        top: viewport.top,
        left: viewport.left,
        width: viewport.width,
        height: viewport.height,
        pointerEvents,
      }}
      data-legacy-ds="Mask"
    >
      <svg
        data-automation-id="popover-mask"
        style={{
          top: viewport.top,
          left: viewport.left,
          width: viewport.width,
          height: viewport.height,
          fill: isMask ? background : backgroundTransparentMask,
          pointerEvents,
          ...unselectable,
          ...style,
        }}
        {...rest}
      >
        <path
          pointerEvents={allowMouseEventsInWindow ? 'fill' : undefined}
          fillRule="evenodd"
          d={cutout(
            relativeToViewport(viewport),
            ...[].concat(
              // @ts-expect-error - TS2769 - No overload matches this call.
              transparentAnchor
                ? padded
                  ? roundAndPad(relativeAnchor)
                  : relativeAnchor
                : emptyArray
            ),
            ...cutouts
          )}
        />
      </svg>
    </div>
  );
};
export default Mask as React.ComponentType<Props>;
